import React from 'react';

/**
 * Shopify/Amazon-style status pill — colored background + label.
 * Replaces emoji-based status indicators (🔴 active, 🟢 idle, etc.) across admin.
 *
 * Usage:
 *   <StatusPill tone="success">Active</StatusPill>
 *   <StatusPill tone="warning">Pending</StatusPill>
 *   <StatusPill tone="critical">Refunded</StatusPill>
 *   <StatusPill tone="info">Shipped</StatusPill>
 *   <StatusPill tone="neutral">Draft</StatusPill>
 */
const TONES = {
  success:  { bg: '#e8f5e9', fg: '#1b5e20', dot: '#2e7d32' },
  warning:  { bg: '#fff8e1', fg: '#856404', dot: '#f59e0b' },
  critical: { bg: '#fdecea', fg: '#b3261e', dot: '#dc3545' },
  info:     { bg: '#e3f2fd', fg: '#0c4a6e', dot: '#0284c7' },
  neutral:  { bg: '#f1f3f5', fg: '#444',    dot: '#6b7280' },
};

// Auto-pick a tone for common status strings so callers don't have to
function inferTone(status) {
  const s = String(status || '').toLowerCase();
  if (['active','paid','delivered','approved','received','published','enabled','ok','success','idle'].includes(s)) return 'success';
  if (['pending','draft','processing','queued','sending','scheduled','partial'].includes(s)) return 'warning';
  if (['cancelled','refunded','rejected','error','failed','disabled','overdue'].includes(s)) return 'critical';
  if (['shipped','sent','viewed','linked'].includes(s)) return 'info';
  return 'neutral';
}

export default function StatusPill({ children, tone, status, dot = false, size = 'md', style }) {
  const t = TONES[tone || inferTone(status || children)] || TONES.neutral;
  const fontSize = size === 'sm' ? 11 : 12;
  const padding = size === 'sm' ? '2px 8px' : '3px 10px';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding,
      borderRadius: 999,
      fontSize,
      fontWeight: 600,
      letterSpacing: 0.2,
      background: t.bg,
      color: t.fg,
      whiteSpace: 'nowrap',
      ...style,
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.dot }} aria-hidden="true" />}
      {children || status}
    </span>
  );
}
